/**
 * The Daybreak icon family.
 *
 * The landing page was drawing from lucide — good icons, but a general-purpose
 * set: Target, Sparkles, Zap. They are the same eight glyphs every SaaS page
 * uses, which means they say "software" and nothing else. A family only reads
 * as a family if it has a rule.
 *
 * The rule here is one line long: EVERY GLYPH CONTAINS EXACTLY ONE ASCENDING
 * DIAGONAL, and it is the thing the icon is about. The check rises. The radar
 * sweeps upward. The bars climb left to right. At 16px you cannot read the
 * detail of an icon, but you can read its direction — so the whole set points
 * the same way the page does.
 *
 * Grid: 24. Stroke: 1.5, round cap and join, never filled. Optical padding of
 * ~2.5 units, so these sit at the same weight as the lucide icons still used in
 * the app chrome and nothing looks like it came from a different box.
 */

function Glyph({ children, className = "w-5 h-5", title }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      // Decorative next to a visible label (the common case here) must be
      // hidden, or a screen reader announces every heading twice. Pass a title
      // only when the icon is genuinely carrying the meaning alone.
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : "true"}
      aria-label={title || undefined}
    >
      {children}
    </svg>
  );
}

/* Keyword gap analysis — a document whose bars climb toward the score. */
export const IconScore = (p) => (
  <Glyph {...p}>
    <path d="M5 21V3h9l5 5v13z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 17.5v-2.5M12 17.5v-4.5M15.5 17.5v-6.5" />
  </Glyph>
);

/* Truthful tailoring — the same lines, lifted. */
export const IconRewrite = (p) => (
  <Glyph {...p}>
    <path d="M3.5 6.5h15M3.5 11.5h9M3.5 17.5h5" />
    <path d="M11 18 20 9" />
    <path d="M20 9h-4.5M20 9v4.5" />
  </Glyph>
);

/* Cover letter — the flap opens upward. */
export const IconLetter = (p) => (
  <Glyph {...p}>
    <path d="M3.5 8.5v11h17v-11" />
    <path d="M3.5 8.5 12 2.5l8.5 6" />
    <path d="M8 13.5h8M8 16.5h5" />
  </Glyph>
);

/* One pipeline — columns, and a line that climbs across them. */
export const IconBoard = (p) => (
  <Glyph {...p}>
    <path d="M3.5 4.5h17v15h-17z" />
    <path d="M9 4.5v15M15 4.5v15" />
    <path d="m6 15.5 3-3 3 1.5 6-5.5" />
  </Glyph>
);

/* Human in the loop — the check rises out of the shield. */
export const IconShield = (p) => (
  <Glyph {...p}>
    <path d="M12 2.5 4.5 5.5v6c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5v-6z" />
    <path d="m8.75 12.25 2.25 2.25 4.5-5" />
  </Glyph>
);

/* Faster, not sloppier — three parallel rises over a baseline. */
export const IconPace = (p) => (
  <Glyph {...p}>
    <path d="M3.5 20.5h17" />
    <path d="m4.5 16.5 4-4M10 16.5l4-4M15.5 16.5l4-4" />
  </Glyph>
);

/* Job Radar — arcs from the corner, and the sweep going up. */
export const IconRadar = (p) => (
  <Glyph {...p}>
    <path d="M4 14.5a6 6 0 0 1 6 6" />
    <path d="M4 9.5a11 11 0 0 1 11 11" />
    <path d="M4 4.5a16 16 0 0 1 16 16" />
    <path d="M4 20.5 15 9.5" />
    <circle cx="17" cy="7.5" r="1.4" />
  </Glyph>
);

/* MCP server — a prompt, and the reply climbing out of it. */
export const IconTerminal = (p) => (
  <Glyph {...p}>
    <path d="M3.5 4.5h17v15h-17z" />
    <path d="M3.5 8.5h17" />
    <path d="m7 12 2.5 2.5L7 17" />
    <path d="m12.5 17 4.5-4.5" />
  </Glyph>
);

/* The list mark. A check that ascends past where it started — the same shape
   the rest of the family is built on, so bullets belong to the set instead of
   being borrowed from the UI kit. */
export const IconRise = (p) => (
  <Glyph {...p}>
    <path d="m4.5 12.5 4.5 4.5 10.5-11" />
  </Glyph>
);

/* Call-to-action arrow. 45°, because on this page forward and upward are the
   same direction. */
export const IconArrowRise = (p) => (
  <Glyph {...p}>
    <path d="M5.5 18.5 18.5 5.5" />
    <path d="M18.5 5.5H10M18.5 5.5V14" />
  </Glyph>
);
